<template>
  <v-autocomplete
    v-model="lazyValue"
    :items="countries"
    v-bind="{ label, hint, ...inputProps }"
    item-text="name"
    item-value="id"
    hide-no-data
    :clearable="clearable"
    :data-test="dataTest"
    outlined
    no-filter
    :class="[selectMixinClass, '']"
    append-icon="$iconSearch"
    @change="
      $event => [inputVisibility($event, 'change'), $emit('change', $event)]
    "
    @focus="focused"
    @blur="[inputVisibility, (search = null)]"
    @update:search-input="search = $event"
    @click:clear="clear()"
  >
    <template #selection="{ item: country, index }">
      {{ country.name
      }}{{ inputProps.multiple && index != lazyValue.length - 1 ? ", " : "" }}
    </template>
    <template #item="{ item: country, attrs, on }">
      <v-list-item v-bind="attrs" v-on="on">
        <v-list-item-action v-if="inputProps.multiple === true">
          <v-simple-checkbox
            :value="manualSelected(country)"
            @input="manualInput(country)"
          />
        </v-list-item-action>
        <v-list-item-content class="d-flex">
          <span
            v-if="country.alpha2"
            :class="`flag-icon fi fi-${country.alpha2.toLowerCase()} pr-2 mr-2`"
            style="flex: 0 0 auto;"
          />
          {{ country.name }}
          <template v-if="country.ioc">({{ country.ioc }})</template>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>
<script>
import { defineComponent } from "vue";
import { useCountryHelper } from "@composables/masterdata/countries";
import { getFederationConfiguration } from "@composables/masterdata/config";
import selectsMixin from "./selects_mixin";
import * as JsSearch from "js-search";
import { clone } from "lodash-es";
import gql from "fraql";

export default defineComponent({
  name: "AppSelectCountries",
  mixins: [selectsMixin],
  props: {
    prepend: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: function() {
        return this.$t("calendar:filters.country.label", "Country");
      }
    },
    hint: {
      type: String,
      default: null
    },
    federationCountryDefaultSelected: {
      type: Boolean,
      default: false
    },
    dataTest: {
      type: String,
      default: "countries"
    },
    inputProps: {
      type: Object,
      default: () => ({})
    }
  },
  setup() {
    const { masterCountries } = useCountryHelper();
    const { masterConfiguration } = getFederationConfiguration();
    return {
      masterCountries,
      masterConfiguration
    };
  },
  data() {
    return {
      search: "",
      menuProperties: {}
    };
  },
  computed: {
    countries() {
      let masterCountries = clone(this.masterCountries);
      if (this.federationCountryDefaultSelected) {
        // Push federation's country to first in line
        const federationCountryId = this.masterConfiguration.countryId;
        const federationCountry = this.masterCountries.find(
          country => country.id == federationCountryId
        );
        masterCountries = masterCountries.filter(
          country => country.id != federationCountryId
        );
        masterCountries.unshift(federationCountry);
      }

      if (this.prepend) masterCountries = this.prepend.concat(masterCountries);

      if (this.search && this.search.length > 0) {
        const s = new JsSearch.Search("id");
        s.addIndex("name");
        s.addIndex("ioc");
        s.addDocuments(masterCountries);
        masterCountries = s.search(this.search);
      }

      return masterCountries;
    },
    lazyValue: {
      get() {
        if (!this.value && this.inputProps.multiple === true) {
          return [];
        }
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    }
  },
  mounted() {
    this.preselectFederation();
  },
  methods: {
    clear() {
      /*if (this.inputProps.multiple) {
        this.lazyValue = this.lazyValue.filter(x => !x.id);
        return;
      }
      this.lazyValue = null;*/
    },
    preselectFederation() {
      if (this.federationCountryDefaultSelected) {
        if (!this.lazyValue || this.lazyValue.length == 0) {
          // Make federation country automatically selected as default
          const federationCountry = this.masterCountries.find(
            country => country.id == this.masterConfiguration.countryId
          );
          this.manualInput(federationCountry);
        }
      }
    },
    manualSelected(country) {
      if (this.inputProps.multiple) {
        if (this.inputProps.returnObject) {
          return this.lazyValue.some(x => x.id == country.id);
        }
        return this.lazyValue.some(xId => xId == country.id);
      }

      if (this.inputProps.returnObject) {
        return this.lazyValue && this.lazyValue.id == country.id;
      }

      return this.lazyValue == country.id;
    },
    manualInput(country) {
      if (this.inputProps.multiple) {
        if (this.inputProps.returnObject) {
          if (this.lazyValue.some(c => c.id == country.id)) {
            this.lazyValue = this.lazyValue.filter(c => c.id != country.id);
            return;
          }
          // After clicking clear and selecting new countries in middle of apollo query, .push does nothing until query is completed
          // .concat makes it work
          this.lazyValue = this.lazyValue.concat([country]);
          return;
        }
        if (this.lazyValue.some(cId => cId == country.id)) {
          this.lazyValue = this.lazyValue.filter(cId => cId != country.id);
          return;
        }
        this.lazyValue = this.lazyValue.concat([country.id]);
        return;
      }

      if (this.inputProps.returnObject) {
        if (this.lazyValue?.id == country.id) {
          this.lazyValue = null;
          return;
        }
        this.lazyValue = country;
        return;
      }

      if (this.lazyValue == country.id) {
        this.lazyValue = null;
        return;
      }

      this.lazyValue = country.id;
    }
  },
  fragments: {
    countryFields: gql`
      fragment _ on Country {
        id
        name
        alpha2
        ioc
      }
    `
  }
});
</script>
